import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface ConfirmationDialogData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  icon?: string;
  iconColor?: string;
  highlightColor?: string;
  confirmColor?: string;
  cancelVariant?: 'destructive';
  /** Optional headed groups of detail lines under the message, e.g. a preview of what a save produces. */
  sections?: ConfirmationDialogSection[];
}

export interface ConfirmationDialogSection {
  heading: string;
  items: string[];
}

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <div class="confirmation-dialog">
      <div class="dialog-header">
        @if (data.icon) {
          <div
            class="icon-container"
            [style.background]="data.highlightColor || '#fef3c7'"
          >
            <mat-icon [style.color]="data.iconColor || '#f59e0b'">
              {{ data.icon }}
            </mat-icon>
          </div>
        }
        <h2 mat-dialog-title>{{ data.title }}</h2>
      </div>
      <mat-dialog-content>
        <p>{{ data.message }}</p>
        @if (data.sections?.length) {
          <div class="detail-sections">
            @for (section of data.sections; track section.heading) {
              <section class="detail-section">
                <h3 class="detail-heading">{{ section.heading }}</h3>
                <ul class="detail-list">
                  @for (item of section.items; track $index) {
                    <li>{{ item }}</li>
                  }
                </ul>
              </section>
            }
          </div>
        }
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button
          mat-stroked-button
          (click)="onCancel()"
          class="cancel-button"
          [class.cancel-destructive]="data.cancelVariant === 'destructive'"
          [disabled]="isLoading"
        >
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button
          mat-raised-button
          (click)="onConfirm()"
          class="confirm-button"
          [style.background-color]="data.confirmColor || null"
          [disabled]="isLoading"
        >
          <div class="button-content">
            @if (isLoading) {
              <mat-spinner diameter="16" class="button-spinner"></mat-spinner>
            }
            <span [style.font-style]="isLoading ? 'italic' : 'normal'">{{
              isLoading ? 'Saving' : data.confirmText || 'Confirm'
            }}</span>
          </div>
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .confirmation-dialog {
        padding: 0;

        .dialog-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 24px 24px 0 24px;

          .icon-container {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 16px;

            mat-icon {
              font-size: 36px;
              width: 36px;
              height: 36px;
            }
          }

          h2 {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
            color: #1f2937;
          }
        }

        mat-dialog-content {
          padding: 16px 24px;
          text-align: center;

          p {
            margin: 0;
            line-height: 1.6;
            color: #6b7280;
            font-size: 15px;
          }

          // Headed groups share one width so their lists line up under each other.
          .detail-sections {
            display: inline-flex;
            flex-direction: column;
            gap: var(--spacing-lg);
            min-width: 60%;
            margin-top: var(--spacing-lg);
            text-align: left;

            .detail-heading {
              margin: 0 0 var(--spacing-xs);
              font-size: var(--font-size-sm);
              font-weight: 600;
              letter-spacing: var(--badge-letter-spacing);
              text-transform: uppercase;
              color: var(--text-gray-500);
            }

            .detail-list {
              margin: 0;
              padding: var(--spacing-md) var(--spacing-xl);
              list-style: none;
              background-color: var(--surface-gray);
              border-radius: var(--radius-md);

              // Items can be free text (a manager note), so keep its line breaks and wrap long
              // unbroken words rather than letting them overflow the dialog.
              li {
                color: var(--text-gray-700);
                font-size: var(--font-size-base);
                line-height: 1.8;
                white-space: pre-wrap;
                overflow-wrap: anywhere;
              }
            }
          }
        }

        mat-dialog-actions {
          padding: 0 24px 24px 24px;
          gap: 12px;
          justify-content: space-between;

          button {
            min-width: 120px;
            border-radius: 12px;
            font-weight: 500;
            text-transform: none;
            font-size: 14px;
          }

          .cancel-button {
            border-color: #d1d5db;
            color: #6b7280;

            &:hover:not([disabled]) {
              background-color: #f9fafb;
              border-color: #9ca3af;
            }

            &[disabled] {
              opacity: 0.5;
              cursor: not-allowed;
              background-color: #f3f4f6;
              color: #9ca3af;
              border-color: #e5e7eb;
            }

            &.cancel-destructive {
              border-color: rgba(159, 50, 35, 0.4);
              color: var(--dark-red-error);

              &:hover:not([disabled]) {
                background-color: rgba(159, 50, 35, 0.05);
                border-color: rgba(159, 50, 35, 0.6);
              }
            }
          }

          .confirm-button {
            background-color: var(--primary-color);
            color: white;
            transition: all 0.2s ease;

            &:hover:not([disabled]) {
              background-color: var(--primary-dark);
            }

            &[disabled] {
              opacity: 0.65;
              cursor: not-allowed;
              background-color: #9ca3af !important;
              box-shadow: none;
            }

            .button-content {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            }

            .button-spinner {
              display: inline-block;
              line-height: 0;

              ::ng-deep circle {
                stroke: white;
              }
            }
          }
        }
      }

      // Override Material Dialog default padding
      ::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface {
        border-radius: 16px;
      }
    `,
  ],
})
export class ConfirmationDialogComponent {
  dialogRef = inject<MatDialogRef<ConfirmationDialogComponent>>(MatDialogRef);
  data = inject<ConfirmationDialogData>(MAT_DIALOG_DATA);

  isLoading = false;

  constructor() {
    // Prevent closing while loading
    this.dialogRef.disableClose = true;
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    if (!this.isLoading) {
      this.dialogRef.close(false);
    }
  }
}
